import { record, upper, type Row } from '../ability/context';
import { readItemField } from './item-field-reader';
import { readItemUuidSlot } from './inventory-raw-readers';
import { constructDefinitionSourceFromBase } from '../port/k1-compile/from-base/definition-source';
import { constructPlayerEncounterSourceFromBase } from './from-base-player-source';
import { constructEncounterSourcePictures, constructPlayerDefinitionPictures } from './from-base-player-media';
import { constructClassificationFromBase } from './from-base-classification';
import { createFromBaseNpcContext, constructNpcThroughTiersFromBase, constructNpcStatusEnvironmentFromBase,
    constructNpcNaturalEquipmentFromBase, constructNpcTemplateSizeFromBase, constructNpcNestedGrantsFromBase,
    constructNpcIntegersFromBase, createFromBaseNpcGrantContext, type FromBaseNpcMetadata } from './from-base-npc-stages';
import { constructNpcForcedSkillsFromBase } from './from-base-npc-forced-skills';
import { constructNpcFullSkillEntriesFromBase } from './from-base-npc-full-skills';
import { constructNpcFeaturesFromBase } from './from-base-npc-features';
import { constructNpcDefenseSeedsFromBase, constructNpcDefenseGrantsFromBase } from './from-base-npc-defenses';
import { constructNpcArmorClassFromBase } from './from-base-npc-ac';
import { constructNpcResourcesFromBase } from './from-base-npc-resources';
import { constructNpcTemplateAbilitiesFromBase } from './from-base-npc-abilities';
import { constructNpcFeatIntegersFromBase } from './from-base-npc-feat-integers';
import { constructNpcPostFromBase } from './from-base-npc-post';
import { constructNpcCastableFromBase } from './from-base-npc-castable';
import { constructInventoryDefinitionReferences } from './from-base-inventory-references';
import { finalizeInventoryDefinitionLayerSurface } from './from-base-inventory-layer-surface';
import { normalizeInventoryDefinition } from './from-base-inventory-normalization';
import { deriveDefaultProviderNames } from './provider-catalog-order';
import { findInventoryRow } from '../inventory/flag-adjust';
import { createCreatureShapeshiftCompiler } from './creature-shapeshift';

/** Only the NPC's form/gear-owned source window. Definitions and saved gear
 * are borrowed from the held page, never compiled or fetched here. The normal
 * NPC stages own NPC tiers, native abilities and spellcasting throughout. */
export function constructNpcFormRemovalWindow(base: Row, metadata: FromBaseNpcMetadata, inventory: Row[],
    definition: (id: string) => Row | null, applyForm = false): Row {
    const inventoryMetadata = metadata.inventoryMetadata!;
    metadata = { ...metadata, definitionLookup: definition, savedInventory: () => inventory,
        inventoryLookup: id => findInventoryRow({ inventory }, id) };
    const context = createFromBaseNpcContext(base, metadata, inventoryMetadata);
    const templateId = readItemUuidSlot(readItemField(record(base.template), 'item_id'));
    if (!templateId) throw new Error('npc-template-source-not-held');
    const source = context.rawDefinition(upper(templateId));
    if (!source) throw new Error('npc-template-source-not-held');
    let host = constructDefinitionSourceFromBase(source);
    (host.items as Row).item_type = 'creature';
    host.encounter_creatures = constructEncounterSourcePictures(constructPlayerEncounterSourceFromBase(base.encounter_creatures as Row,
        record(base.encounter_context), base.caster_summons as Row[] | undefined),
        [...Array.isArray(base.pictures) ? base.pictures : [], ...Array.isArray(base.picture_sources) ? base.picture_sources : []], metadata.cdnUrl);
    // Resource height uses the same native portrait selection as initial load.
    const pictures = constructPlayerDefinitionPictures(source, metadata.cdnUrl, context.readers.layerValue);
    if (pictures.length) host.pictures = { item: pictures };
    host = constructClassificationFromBase(base, inventoryMetadata, host, context);
    const bucket = host.item_layer_types as Row;
    for (const key of [...deriveDefaultProviderNames(metadata.layerCatalog), ...metadata.abilityTypes,
        'feat', 'status_effect_permanent', 'spell_damage_resistance', 'spell_damage_vulnerability']) {
        if (!(key in bucket)) continue;
        host[key] = bucket[key]; delete bucket[key];
    }
    host = constructNpcThroughTiersFromBase(base, metadata, inventoryMetadata, host);
    host = constructNpcStatusEnvironmentFromBase(base, metadata, inventoryMetadata, host);
    host = constructNpcNaturalEquipmentFromBase(base, metadata, inventoryMetadata, host);
    if (applyForm) {
        const graft = createFromBaseNpcGrantContext(base, metadata, host);
        host = createCreatureShapeshiftCompiler({ ...graft.common, metadata: metadata.shapeshift, queryFullItem: definition,
            applyMonsterTemplate: () => { throw new Error('form-template-not-ported'); },
            applyCreatureModelMedia: () => { throw new Error('form-model-media-not-ported'); },
            picturesOf: () => { throw new Error('form-pictures-not-ported'); } })(host) as Row;
    } else host = constructNpcTemplateSizeFromBase(base, metadata, inventoryMetadata, host);
    host = constructNpcNestedGrantsFromBase(base, metadata, inventoryMetadata, host);
    host = constructNpcIntegersFromBase(base, metadata, inventoryMetadata, host);
    const forced = constructNpcForcedSkillsFromBase(base, metadata, host);
    host = constructNpcFullSkillEntriesFromBase(base, metadata, forced.row, forced.forced).row;
    host = constructNpcFeaturesFromBase(base, metadata, host);
    host = constructNpcDefenseGrantsFromBase(base, metadata, constructNpcDefenseSeedsFromBase(base, metadata, host));
    host = constructNpcArmorClassFromBase(base, metadata, host);
    host = constructNpcResourcesFromBase(base, metadata, host);
    host = constructNpcTemplateAbilitiesFromBase(base, metadata, host);
    host = constructNpcFeatIntegersFromBase(base, metadata, host);
    // Mirror only this owned rule window, using already completed held
    // definitions. The entry finalizer's media, PL, catalog and cache passes
    // never run on a form delta.
    const names = Object.keys(metadata.layerCatalog);
    const references = inventoryMetadata;
    const expand = (row: Row) => constructInventoryDefinitionReferences(row, references, context.rawDefinition,
        { fullDefinition: id => definition(id) });
    const root = expand({ items: host.items, item_layer_types: Object.fromEntries(names.filter(key => key in host).map(key => [key, host[key]])) });
    Object.assign(host, root.item_layer_types);
    host = expand(host);
    for (const key of names) if (key in host) {
        if (!(key in (host.item_layer_types as Row))) (host.item_layer_types as Row)[key] = host[key];
        delete host[key];
    }
    host = finalizeInventoryDefinitionLayerSurface(host, inventoryMetadata.definitionLayerOrder);
    host = constructNpcPostFromBase(base, { ...metadata, postReference: true }, host);
    host = normalizeInventoryDefinition(host, metadata.layerCatalog);
    constructNpcCastableFromBase(base, metadata, host);
    return host;
}
